/* istanbul ignore file */
/* eslint-disable */
import React from "react";
import "./scenario-table.scss";
import Heading from "../components/Heading";
import {
  MotiveringCompleetOutput,
  ToelichtingOptions,
  ToelichtingRegel,
} from "../../../.generated/rapportage/rapportagetypes";
import { TableNettoBesteedbaarInkomen } from "./nbi";
import { MaximaleHypotheekTable } from "./tables/maximale-hypotheek";
import {
  CitationNote,
  CitationProvider,
  excludedFootnotes,
} from "./citation-notes";
import { ControleMaandlastOptions, getNaam } from "./infra/utils";
import { BenodigdKapitaalTable } from "./tables/benodigd-kapitaal-table";
import { ControleWerkelijkeLastenTable } from "./tables/controle-werkelijke-lasten-table";
import { BrutoInkomensWensenTable } from "./tables/bruto-inkomens-wensen";
import { BenodigdInkomenTable } from "./tables/benodigd-inkomen-table";
import { ControleMaandlastTable } from "./tables/controle-maandlast-table";
import Table from "../components/Table";
import { Column } from "../components/BaseTable";
import {
  ControleGewenstInkomenOptions,
  DashboardScenarioPensioen,
} from "../../../.generated/rapportage/rapportagetypes";
import { CitationRef } from "./citation-notes";
import { getMonthlyAmount } from "./infra/scenario-utils";

export type PensioenProps = {
  motiveringOutput: MotiveringCompleetOutput | null;
  dashboardSettings: DashboardScenarioPensioen;
  selectedAanvrager?: "Aanvrager1" | "Aanvrager2";
};

const monthDivisionFactor = 12;

export const InkomensanalyseTabelPensioen: React.FC<PensioenProps> = ({
  motiveringOutput,
  dashboardSettings,
  selectedAanvrager,
}) => {
  const nbiWeergeven =
    (dashboardSettings.uitgangspunten?.controleGewenstInkomen
      ?.controleGewenstInkomen === ControleGewenstInkomenOptions.Nbi &&
      !!dashboardSettings.uitgangspunten?.controleHypotheeknorm &&
      !!dashboardSettings.uitgangspunten?.controleGewenstInkomen
        ?.gewijzigdeJarenTonen) ||
    false;
  const maximaleHypotheekWeergeven =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleerMaximaleHypotheek || false;
  const benodigdKapitaalWeergeven =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.benodigdKapitaalTonen || false;
  const controleWerkelijkeLastenWeergeven =
    (dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast ||
      false) &&
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast === ControleMaandlastOptions.WerkelijkeLast;
  const brutoInkomensWensenWeergeven =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.controleGewenstInkomen === "BrutoInkomenswens";
  const brutoInkomensWensPercentage =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.brutoInkomenswensPercentage || 0;
  const controleMaandlast =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast || false;
  const controleMaandlastKeuze =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast || null;

  if (
    !motiveringOutput?.motiveringInkomensanalyse ||
    !motiveringOutput.motiveringInkomensanalyse.resultaten?.[0]
  ) {
    return null;
  }

  const motiveringInkomensanalyse = motiveringOutput.motiveringInkomensanalyse;
  const algemeenResultaat =
    motiveringOutput.motiveringInkomensanalyse.resultaten?.[0];
  const normGegevens = motiveringOutput.normGegevens;
  const voetnotenArray = motiveringOutput.toelichtingRegels || [];

  const resultaatPeriodeEerste = motiveringInkomensanalyse.resultaten?.[0];

  // Determine current aanvrager and partner based on selectedAanvrager
  const currentAanvrager =
    selectedAanvrager === "Aanvrager2"
      ? motiveringOutput.klantNaamPartner ||
        getNaam(motiveringInkomensanalyse.aanvrager2, "Aanvrager 2")
      : motiveringOutput.klantNaamAanvrager ||
        getNaam(motiveringInkomensanalyse.aanvrager1, "Aanvrager 1");
  const currentPartner =
    selectedAanvrager === "Aanvrager2"
      ? motiveringOutput.klantNaamAanvrager ||
        getNaam(motiveringInkomensanalyse.aanvrager1, "Aanvrager 2")
      : motiveringOutput.klantNaamPartner ||
        getNaam(motiveringInkomensanalyse.aanvrager2, "Aanvrager 1");
  const currentClientName =
    selectedAanvrager === "Aanvrager1"
      ? motiveringOutput.klantNaamAanvrager
      : motiveringOutput.klantNaamPartner;

  // Get the correct aanvrager and partner data based on selectedAanvrager
  const primaryAanvragerResultaat =
    selectedAanvrager === "Aanvrager2"
      ? resultaatPeriodeEerste?.aanvrager2
      : resultaatPeriodeEerste?.aanvrager1;
  const secondaryAanvragerResultaat =
    selectedAanvrager === "Aanvrager2"
      ? resultaatPeriodeEerste?.aanvrager1
      : resultaatPeriodeEerste?.aanvrager2;

  const aowInkomenAanvrager = primaryAanvragerResultaat?.aowUitkeringBedrag;
  const beleggingInkomenAanvrager =
    (primaryAanvragerResultaat?.beleggingenBedrag || 0) +
    (primaryAanvragerResultaat?.beleggingenBruteringBedrag || 0);
  const verhuurInkomenAanvrager =
    (primaryAanvragerResultaat?.verhuurBedrag || 0) +
    (primaryAanvragerResultaat?.verhuurBruteringBedrag || 0);
  const expirerendeLijfrenteAanvrager =
    (primaryAanvragerResultaat?.expirerendeLijfrenteBedrag || 0) +
    (primaryAanvragerResultaat?.expirerendeLijfrenteBruteringBedrag || 0);
  const werkgeversPensioenAanvrager =
    primaryAanvragerResultaat?.werkgeverspensioenBedrag;

  const aowInkomenPartner = secondaryAanvragerResultaat?.aowUitkeringBedrag;
  const beleggingInkomenPartner =
    (secondaryAanvragerResultaat?.beleggingenBedrag || 0) +
    (secondaryAanvragerResultaat?.beleggingenBruteringBedrag || 0);
  const verhuurInkomenPartner =
    (secondaryAanvragerResultaat?.verhuurBedrag || 0) +
    (secondaryAanvragerResultaat?.verhuurBruteringBedrag || 0);
  const expirerendeLijfrentePartner =
    (secondaryAanvragerResultaat?.expirerendeLijfrenteBedrag || 0) +
    (secondaryAanvragerResultaat?.expirerendeLijfrenteBruteringBedrag || 0);
  const restInkomenPartner = secondaryAanvragerResultaat?.restInkomenBedrag;
  const werkgeversPensioenPartner =
    secondaryAanvragerResultaat?.werkgeverspensioenBedrag;

  const totaalInkomen = algemeenResultaat?.totaalBrutoInkomenBedrag;
  const inkomensverlies =
    motiveringInkomensanalyse.totaalHuidigBrutoJaarinkomenBedrag &&
    totaalInkomen
      ? motiveringInkomensanalyse.totaalHuidigBrutoJaarinkomenBedrag -
        totaalInkomen
      : null;

  const jaarlijkseAnalyseDataForTable = {
    gewenstInkomen: normGegevens?.gewenstBrutoInkomenBedrag ?? null,
    maandelijksTekorten: [
      normGegevens?.tekortGewenstBrutoInkomenBedrag === null ||
      normGegevens?.tekortGewenstBrutoInkomenBedrag === undefined
        ? null
        : normGegevens?.tekortGewenstBrutoInkomenBedrag / monthDivisionFactor,
    ],
  };
  const conclusieTextForTable =
    resultaatPeriodeEerste?.conclusie?.toString() ?? "";

  const nbiDataArray = (
    motiveringInkomensanalyse.nbiOverzichten?.["1"]?.jaren || []
  ).map((jaar) => ({
    ...jaar,
    bijzonderheden: jaar.bijzonderheden
      ? jaar.bijzonderheden.map((b) =>
          b.bijzonderheid === null ? null : { bijzonderheid: b.bijzonderheid }
        )
      : null,
  }));
  const nbiStartDatum = motiveringInkomensanalyse.startdatum;
  const nbiVoetnootText =
    motiveringInkomensanalyse.nbiOverzicht?.voetnoot?.voetnoot || "";

  const benodigdKapitaalBedrag =
    motiveringOutput.benodigdKapitaal?.bedrag ?? null;
  const benodigdKapitaalOmschrijvingText =
    motiveringOutput.benodigdKapitaal?.omschrijving || "";

  const incomeColumns: Column[] = [
    {
      header: "Periode",
      accessor: () => "Bij pensioen",
    },
    {
      header: `AOW ${currentAanvrager}`,
      accessor: () => getMonthlyAmount(aowInkomenAanvrager),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: (
        <>
          Inkomsten uit belegging(en) {currentAanvrager}{" "}
          {(primaryAanvragerResultaat?.beleggingenBruteringBedrag || 0) > 0 && (
            <CitationRef type={ToelichtingOptions.Brutering} />
          )}
        </>
      ),
      accessor: () => getMonthlyAmount(beleggingInkomenAanvrager),
      dataType: "currency" as const,
    },
    {
      header: (
        <>
          Inkomsten uit verhuur {currentAanvrager}{" "}
          {(primaryAanvragerResultaat?.verhuurBruteringBedrag || 0) > 0 && (
            <CitationRef type={ToelichtingOptions.Brutering} />
          )}
        </>
      ),
      accessor: () => getMonthlyAmount(verhuurInkomenAanvrager),
      dataType: "currency" as const,
    },
    {
      header: `Expirerende lijfrente ${currentAanvrager}`,
      accessor: () => getMonthlyAmount(expirerendeLijfrenteAanvrager),
      dataType: "currency" as const,
    },
    {
      header: `Werkgeverspensioen ${currentAanvrager}`,
      accessor: () => getMonthlyAmount(werkgeversPensioenAanvrager),
      dataType: "currency" as const,
    },
    ...(secondaryAanvragerResultaat
      ? [
          {
            header: `AOW ${currentPartner}`,
            accessor: () => getMonthlyAmount(aowInkomenPartner),
            dataType: "currency" as const,
          },
          {
            header: (
              <>
                Inkomsten uit belegging(en) {currentPartner}{" "}
                {(secondaryAanvragerResultaat?.beleggingenBruteringBedrag ||
                  0) > 0 && <CitationRef type={ToelichtingOptions.Brutering} />}
              </>
            ),
            accessor: () => getMonthlyAmount(beleggingInkomenPartner),
            dataType: "currency" as const,
          },
          {
            header: (
              <>
                Inkomsten uit verhuur {currentPartner}{" "}
                {(secondaryAanvragerResultaat?.verhuurBruteringBedrag || 0) >
                  0 && <CitationRef type={ToelichtingOptions.Brutering} />}
              </>
            ),
            accessor: () => getMonthlyAmount(verhuurInkomenPartner),
            dataType: "currency" as const,
          },
          {
            header: `Expirerende lijfrente ${currentPartner}`,
            accessor: () => getMonthlyAmount(expirerendeLijfrentePartner),
            dataType: "currency" as const,
          },
          {
            header: `Inkomen van ${currentPartner}`,
            accessor: () => getMonthlyAmount(restInkomenPartner),
            dataType: "currency" as const,
          },
          {
            header: `Werkgeverspensioen ${currentPartner}`,
            accessor: () => getMonthlyAmount(werkgeversPensioenPartner),
            dataType: "currency" as const,
          },
        ]
      : []),
    {
      header: "Inkomen totaal",
      accessor: () => getMonthlyAmount(totaalInkomen),
      dataType: "currency" as const,
      className: "font-weight-bold",
    },
    {
      header: "Inkomensverlies",
      accessor: () => getMonthlyAmount(inkomensverlies),
      dataType: "currency" as const,
      className: "table-warning",
      violation: (value: number) => value > 0,
    },
  ];

  // Remove incomeData, use a single row (empty object) for the table
  const incomeRow = {};
  const filteredIncomeData = [incomeRow];

  return (
    <CitationProvider
      voetnoten={voetnotenArray}
      excludedFootnotes={excludedFootnotes}
    >
      <div className="container-fluid mt-2 px-2">
        {motiveringInkomensanalyse && (
          <Heading
            text={`Inkomen bij pensioen ${currentClientName}`}
            level={3}
            ignoreNumbers
            ignoreTOC
          />
        )}
        <Table
          columns={incomeColumns}
          data={filteredIncomeData}
          id="inkomen-pensioen-scenario-table"
          rotate
        />

        {brutoInkomensWensenWeergeven && (
          <BrutoInkomensWensenTable
            brutoInkomensWensPercentage={brutoInkomensWensPercentage}
            jaarlijkseAnalyse={jaarlijkseAnalyseDataForTable}
            gewenstInkomenBedrag={
              jaarlijkseAnalyseDataForTable?.gewenstInkomen || 0
            }
          />
        )}

        {controleWerkelijkeLastenWeergeven && (
          <ControleWerkelijkeLastenTable
            toegestaneLastBedragen={[
              normGegevens?.toegestaneLastBedrag ?? null,
            ]}
            werkelijkeLastBedragen={[
              normGegevens?.werkelijkeLastBedrag ?? null,
            ]}
            tekortToegestaneLastBedragen={[
              resultaatPeriodeEerste?.tekortToegestaneLastBedrag ?? null,
            ]}
          />
        )}

        {controleMaandlast &&
          controleMaandlastKeuze ===
            ControleMaandlastOptions.BenodigdInkomen && (
            <BenodigdInkomenTable
              data={[
                {
                  benodigdInkomenBedrag:
                    resultaatPeriodeEerste?.benodigdInkomenBedrag ?? null,
                  tekortBenodigInkomenBedrag:
                    resultaatPeriodeEerste?.tekortBenodigInkomenBedrag ?? null,
                  tekortNettoInkomenBedrag:
                    resultaatPeriodeEerste?.tekortNettoInkomenBedrag ?? null,
                },
              ]}
            />
          )}

        {maximaleHypotheekWeergeven && (
          <MaximaleHypotheekTable
            inkomensanalyseResultaten={
              motiveringInkomensanalyse?.resultaten ?? []
            }
          />
        )}

        {controleMaandlast && (
          <ControleMaandlastTable
            conclusies={[conclusieTextForTable]}
            controleMaandlastKeuze={controleMaandlastKeuze}
          />
        )}

        <div className="mt-3 px-2">
          {voetnotenArray?.map((footnote: ToelichtingRegel) => (
            <CitationNote
              key={footnote.type}
              type={footnote.type}
              text={footnote.toelichting || ""}
            />
          ))}
        </div>

        {nbiWeergeven && (
          <TableNettoBesteedbaarInkomen
            startDatum={nbiStartDatum}
            nbi={nbiDataArray}
            voetnoot={nbiVoetnootText}
          />
        )}

        {benodigdKapitaalWeergeven && (
          <BenodigdKapitaalTable
            benodigdKapitaal={benodigdKapitaalBedrag}
            benodigdKapitaalOmschrijving={benodigdKapitaalOmschrijvingText}
          />
        )}
      </div>
    </CitationProvider>
  );
};
